import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight, CheckCheck, Download, LockKeyhole, MessageCircle, ShieldCheck } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import type { ContactSummary, DataRequest } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { DataTable, Field, Heading, MiniAvatar, Pick, Section } from "@/components/workspace/primitives"
import { api, ApiError } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { contactQuery, contactsQuery, dataRequestsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/settings/consent")({
  validateSearch: z.object({ contact: z.string().optional() }),
  component: ConsentData,
})

const RULES = [
  [MessageCircle, "Support is not marketing consent", "A customer starting a conversation does not grant permission for marketing messages."],
  [ShieldCheck, "Purpose and channel stay together", "Store the source, timestamp, purpose and channel with every consent change."],
  [CheckCheck, "Check again before every send", "Opt-outs and changes in channel eligibility exclude recipients at send time."],
] as const

/** Screen 46. */
function ConsentData() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const admin = can("manage_customer_data")
  const { contact: preselected } = Route.useSearch()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState<ContactSummary | null>(null)
  const [request, setRequest] = useState<"export" | "delete">("export")
  const [open, setOpen] = useState(false)
  const [verified, setVerified] = useState(false)
  const params = { workspace: workspace.slug }
  const results = useQuery({ ...contactsQuery(workspace.slug, { q: query }), enabled: admin && query.trim().length >= 2 })
  const preset = useQuery({ ...contactQuery(workspace.slug, preselected ?? ""), enabled: admin && Boolean(preselected) && !selected })
  const requests = useQuery({ ...dataRequestsQuery(workspace.slug), enabled: admin, refetchInterval: 5000 })
  const target = selected ?? (preset.data ? { id: preset.data.id, displayName: preset.data.displayName, detail: preset.data.detail, identityLabel: preset.data.identityLabel } : null)

  const record = useMutation({
    mutationFn: () => api<DataRequest>(`/w/${workspace.slug}/contacts/data-requests`, { method: "POST", body: JSON.stringify({ contactId: target!.id, type: request, verified: true }) }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "data-requests"] })
      setOpen(false)
      setVerified(false)
      toast.success(created.type === "export" ? "Export request recorded" : "Deletion request recorded", { description: created.type === "export" ? "The export is being prepared and appears below." : "Deletion runs with the retention controls in a later release; the request is in the audit log now." })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not record the request"),
  })

  return (
    <>
      <Heading title="Consent & data" description="Customer permission, with the context that makes it meaningful." />
      <Section title="Permission is specific" description="These safeguards apply to every workspace.">
        <div className="ws-consent-rules">
          {RULES.map(([Icon, title, body]) => (
            <div key={title}>
              <span className="ws-feature-icon">
                <Icon size={19} />
              </span>
              <div>
                <strong>{title}</strong>
                <p>{body}</p>
              </div>
              <LockKeyhole size={14} />
            </div>
          ))}
        </div>
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/contacts" params={params} search={{}}>
            View contact consent records
            <ArrowRight />
          </Link>
        </Button>
      </Section>
      {admin ? (
        <>
          <Section title="Customer data requests" description="Verify identity and scope before preparing a customer export or deletion.">
            <div className="ws-form-grid">
              <Field label="Find a contact">
                {(id) => (
                  <Input
                    id={id}
                    value={selected ? selected.displayName : query}
                    onChange={(e) => {
                      setSelected(null)
                      setQuery(e.target.value)
                    }}
                    placeholder="Name, email or channel identity"
                  />
                )}
              </Field>
              <Pick label="Request type" value={request} options={[{ value: "export", label: "Export customer data" }, { value: "delete", label: "Delete customer data" }]} onChange={setRequest} />
            </div>
            {!selected && results.data && results.data.contacts.length > 0 && (
              <div className="mt-3 flex flex-col gap-1">
                {results.data.contacts.slice(0, 5).map((c) => (
                  <button key={c.id} className="ws-person rounded-md p-2 text-left hover:bg-muted" onClick={() => setSelected(c)}>
                    <MiniAvatar name={c.displayName} />
                    <div>
                      <strong>{c.displayName}</strong>
                      <small>{c.detail ?? c.identityLabel}</small>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="ws-form-footer">
              <p className="ws-hint">Restricted to workspace admins. Requests appear in the audit log.</p>
              <Button
                disabled={!target}
                onClick={() => {
                  setVerified(false)
                  setOpen(true)
                }}
              >
                Review request
                <ArrowRight />
              </Button>
            </div>
          </Section>
          {requests.data && requests.data.length > 0 && (
            <Section title="Recent requests">
              <DataTable
                heads={["Contact", "Request", "Status", "Requested", ""]}
                rowKeys={requests.data.map((r) => r.id)}
                rows={requests.data.map((r) => [
                  r.contactName,
                  r.type === "export" ? "Export customer data" : "Delete customer data",
                  <Badge variant={r.status === "completed" ? "secondary" : "outline"}>{r.status === "recorded" ? "Recorded" : r.status === "processing" ? "Preparing" : r.status === "completed" ? "Ready" : "Failed"}</Badge>,
                  <span className="text-muted-foreground">
                    {formatDateTime(r.createdAt)} · {r.requestedByName}
                  </span>,
                  r.type === "export" && r.status === "completed" ? (
                    <Button variant="link" asChild>
                      <a href={`/api/w/${workspace.slug}/contacts/data-requests/${r.id}/download`}>
                        <Download />
                        Download
                      </a>
                    </Button>
                  ) : null,
                ])}
              />
            </Section>
          )}
        </>
      ) : (
        <p className="ws-hint">Customer data requests are restricted to workspace admins.</p>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review data request</DialogTitle>
            <DialogDescription>Confirm who the request is for and what it includes.</DialogDescription>
          </DialogHeader>
          {target && (
            <div className="ws-dialog-form">
              <div className="ws-person">
                <MiniAvatar name={target.displayName} />
                <div>
                  <strong>{target.displayName}</strong>
                  <small>{target.detail ?? target.identityLabel}</small>
                </div>
              </div>
              <div className="ws-request-summary">
                <p>
                  <span>Request</span>
                  <strong>{request === "export" ? "Export customer data" : "Delete customer data"}</strong>
                </p>
                <p>
                  <span>Scope</span>
                  <strong>Profile, messages and consent records</strong>
                </p>
              </div>
              <p className="ws-hint">Review connected identities on the profile before handling the request.</p>
              <label className="ws-checkbox-text">
                <Checkbox checked={verified} onCheckedChange={(v) => setVerified(v === true)} />I have verified the customer’s identity and request scope.
              </label>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button disabled={!verified || record.isPending} onClick={() => record.mutate()}>
                  Record request
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
